import { Loader } from '@busybusy/webapp-react-ui';
import { useToastProblemOpen } from 'contexts/ToastContext';
import { Suspense, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { captureSentryError } from 'utils/captureSentryError';

export interface ILazyImportWrapperProps {
  /**
   * @description Once triggerLoad becomes true, the `LazyComponent` will be loaded lazily.
   */
  triggerLoad?: boolean;

  /**
   * @description optionally, control if the `LazyComponent` should be mounted.
   */
  condition?: boolean;

  /**
   * @description The content to render while the `LazyComponent` is being loaded.
   *
   * the default is `<Loader isOpen={true} />`
   */
  fallback?: React.ReactNode;

  /**
   * @description Catch every error from `children`, not just import failures.
   *
   * Only needed where no boundary sits above; anything rendered under a route already has one.
   */
  catchErrors?: boolean;

  /**
   * @description What to render when `catchErrors` catches. Defaults to nothing.
   */
  errorFallback?: React.ReactNode;
}

const defaultLazyImportWrapperProps = {
  triggerLoad: true,
  condition: true,
  catchErrors: false,
};

// Separate component so `useToastProblemOpen` only runs where `catchErrors` is set. Called
// unconditionally in the wrapper it would make ToastContext a requirement for every consumer,
// since the hook throws when the provider is absent.
const LazyImportErrorBoundary: React.FC<PropsWithChildren<Pick<ILazyImportWrapperProps, 'errorFallback'>>> = ({
  errorFallback,
  children,
}) => {
  const openProblemToast = useToastProblemOpen();

  return (
    <ErrorBoundary
      fallback={<>{errorFallback}</>}
      onError={(error) => {
        captureSentryError(error, 'lazy-import', 'LazyImportWrapper');
        openProblemToast();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * @name LazyImportWrapper
 *
 * @description
 *  Helper component for preventing the network from loading a component until it is needed.
 *
 * Example Usage:
```tsx
  const MyLazyDialog = lazy(() => import('./MyLazyDialog'));
  ...
  <LazyImportWrapper triggerLoad={dialog.isOpen}>
    <MyLazyDialog isOpen={dialog.isOpen} onClose={dialog.close} />
  </LazyImportWrapper>
```
 *
 */
export const LazyImportWrapper: React.FC<PropsWithChildren<ILazyImportWrapperProps>> = (props) => {
  const { triggerLoad, fallback, catchErrors, errorFallback, children, condition } = {
    ...defaultLazyImportWrapperProps,
    ...props,
  };

  const [isMounted, setIsMounted] = useState(false);
  const suspenseFallback = useMemo(() => fallback ?? <Loader isOpen={true} />, [fallback]);

  useEffect(() => {
    if (triggerLoad && !isMounted) {
      setIsMounted(true);
    }
  }, [triggerLoad, isMounted]);

  if (!isMounted || !condition) {
    return null;
  }

  const content = <Suspense fallback={suspenseFallback}>{children}</Suspense>;

  return catchErrors ? (
    <LazyImportErrorBoundary errorFallback={errorFallback}>{content}</LazyImportErrorBoundary>
  ) : (
    content
  );
};
